import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Plus, Search, ArrowLeft, Phone, Mail, MapPin, 
  Edit2, Trash2, X, Building2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { securityGuardsAPI } from '../services/api';

interface SecurityGuard {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  caseta: string;
  shift: 'day' | 'night' | 'mixed';
  status: 'active' | 'inactive';
}

// Mock data removed — page fetches real data from securityGuardsAPI

export default function PersonalSeguridad() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [guards, setGuards] = useState<SecurityGuard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingGuard, setEditingGuard] = useState<SecurityGuard | null>(null);

  const { isAdmin } = useAuth();

  useEffect(() => {
    loadGuards();
  }, []);

  const loadGuards = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await securityGuardsAPI.getAll(condoId);
      const data = response.data || response;
      setGuards(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading guards:', err);
      setGuards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar este vigilante?')) {
      try {
        await securityGuardsAPI.update(id, { status: 'inactive' });
        setGuards(prev => prev.filter(g => g.id !== id));
      } catch (err) {
        console.error('Error deleting guard:', err);
      }
    }
  };

  const getShiftLabel = (shift: string) => {
    const labels: Record<string, string> = { day: 'Día', night: 'Noche', mixed: 'Mixto' };
    return labels[shift] || shift;
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748]">Personal de Seguridad</h1>
              <p className="text-xs text-gray-500">Vigilantes asignados</p>
            </div>
          </div>
          {isAdmin() && (
            <button onClick={() => setShowNewModal(true)} className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Agregar Vigilante
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="grid gap-4">
          {guards.map(guard => (
            <div key={guard.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-[#2D3748]">{guard.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${guard.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                        {guard.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><Building2 className="w-4 h-4" /> {guard.company}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-4 h-4" /> {guard.caseta}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-4 h-4" /> {guard.phone}</p>
                    <p className="text-sm text-gray-500">Turno: <span className="font-medium">{getShiftLabel(guard.shift)}</span></p>
                  </div>
                </div>
                {isAdmin() && (
                  <div className="flex gap-2">
                    <button onClick={() => setEditingGuard(guard)} className="p-2 text-gray-400 hover:text-[#36CCD8]"><Edit2 className="w-5 h-5" /></button>
                    <button onClick={() => handleDelete(guard.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-5 h-5" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {(showNewModal || editingGuard) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">{editingGuard ? 'Editar' : 'Nuevo'} Vigilante</h3>
            <form className="space-y-4">
              <input type="text" placeholder="Nombre completo" defaultValue={editingGuard?.name} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="email" placeholder="Email" defaultValue={editingGuard?.email} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="tel" placeholder="Teléfono" defaultValue={editingGuard?.phone} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Dirección" defaultValue={editingGuard?.address} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Empresa" defaultValue={editingGuard?.company} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <input type="text" placeholder="Caseta asignada" defaultValue={editingGuard?.caseta} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" />
              <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                <option value="day">Turno Día</option>
                <option value="night">Turno Noche</option>
                <option value="mixed">Turno Mixto</option>
              </select>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowNewModal(false); setEditingGuard(null); }} className="flex-1 py-3 border border-gray-200 rounded-xl">Cancelar</button>
                <button type="button" onClick={() => { alert('El módulo de Personal (legacy) ya no guarda registros. Los vigilantes se dan de alta como usuarios desde el módulo de Personal (Empleados).'); }} className="flex-1 py-3 bg-[#36CCD8] text-white rounded-xl">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
